import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  FileText, 
  CheckSquare, 
  BookOpen, 
  FileSpreadsheet, 
  FileCheck,
  ArrowDown,
  Calendar,
  Users,
  Shield
} from 'lucide-react';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import DashboardSidebar from '@/components/DashboardSidebar';
import DashboardTopNav from '@/components/DashboardTopNav';
// import { ContactMessageService } from '@/integrations/supabase/services';
import '@/styles/animations.css';

const ICON_MAP: Record<string, any> = {
  CheckSquare,
  BookOpen,
  FileSpreadsheet,
  Calendar,
  FileCheck,
  Users,
  Shield,
  FileText,
};

const COLOR_MAP: Record<string, string> = {
  'Checklists': 'bg-blue-500/10',
  'Guides': 'bg-green-500/10',
  'Forms': 'bg-purple-500/10',
};

const ICON_COLOR_MAP: Record<string, string> = {
  'Checklists': 'text-blue-600',
  'Guides': 'text-green-600',
  'Forms': 'text-purple-600',
};

const Resources = () => {
  const [resources, setResources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true);
      try {
        // For now, we'll use mock data since we don't have a resources collection
        // You can create a ResourcesService later if needed
        setResources([]);
      } catch (error) {
        console.error('Error fetching resources:', error);
        setResources([]);
      }
      setLoading(false);
    };
    fetchResources();
  }, []);

  const categories = [
    { id: 'all', name: 'All Resources', count: resources.length },
    { id: 'checklists', name: 'Checklists', count: resources.filter(r => r.category === 'Checklists').length },
    { id: 'guides', name: 'Guides', count: resources.filter(r => r.category === 'Guides').length },
    { id: 'forms', name: 'Forms', count: resources.filter(r => r.category === 'Forms').length }
  ];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(resource => resource.category?.toLowerCase() === selectedCategory);

  const handleDownload = (resource: any) => {
    // In a real application, this would trigger an actual file download
    // For now, we'll simulate the download
    console.log(`Downloading ${resource.title}`);
    // You can implement actual file download logic here
    // For example: window.open(resource.file_url, '_blank');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <SidebarInset className="flex-1 flex flex-col">
          <DashboardTopNav pageTitle="Resources" />
          <main className="flex-1 p-4 sm:p-8 mt-[3.5rem] sm:mt-[4.2rem] overflow-auto">
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-4 font-alumni">Resources</h1>
              <p className="text-lg text-muted-foreground font-hind">
                Download helpful documents, checklists, and guides for your Polish residence card application
              </p>
            </div>

            {/* Category Filter */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="font-hind"
                  >
                    {category.name}
                    <Badge variant="secondary" className="ml-2">
                      {category.count}
                    </Badge>
                  </Button>
                ))}
              </div>
            </div>

            {/* Resources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="text-center col-span-full">Loading...</div>
              ) : filteredResources.map((resource) => {
                const IconComponent = ICON_MAP[resource.icon] || FileText;
                const color = resource.color || COLOR_MAP[resource.category] || 'bg-blue-500/10';
                const iconColor = resource.iconColor || ICON_COLOR_MAP[resource.category] || 'text-blue-600';
                return (
                  <Card key={resource.id} className="group hover:shadow-lg transition-all duration-300">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className={`p-3 rounded-lg ${color}`}>
                          <IconComponent className={`h-6 w-6 ${iconColor}`} />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {resource.file_type || resource.fileType || 'PDF'}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-alumni mt-4">{resource.title}</CardTitle>
                      <CardDescription className="font-hind text-sm">
                        {resource.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>File size: {resource.file_size || resource.fileSize}</span>
                          <span>{resource.downloads} downloads</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            Updated: {resource.updated ? new Date(resource.updated).toLocaleDateString() : ''}
                          </span>
                          <Button
                            onClick={() => handleDownload(resource)}
                            size="sm"
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Empty State */}
            {!loading && filteredResources.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No resources found</h3>
                <p className="text-muted-foreground font-hind">
                  No resources match the selected category.
                </p>
              </div>
            )}

            {/* Help Section */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="text-xl font-alumni">Need Help?</CardTitle>
                <CardDescription className="font-hind">
                  Can't find what you're looking for? Our support team is here to help.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="outline" className="flex-1">
                    Contact Support
                  </Button>
                  <Button variant="outline" className="flex-1">
                    Visit Knowledge Base
                  </Button>
                </div>
              </CardContent>
            </Card>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Resources; 